<script context="module" lang="ts">
	export const USE_CVV = true;
	//Used to ensure only one CenPOS component can be loaded
	let id = 0;

	let portholeLoading = false;
	let portholeLoaded = false;

	export let onLoading: Function;
	export let onLoaded: Function;

	export let amount: string;
	export function setAmount(_amount: string) {
		amount = _amount;
	}

	export let transactionType: 'Auth' | 'Sale' = 'Sale';
	export let address = {};

	export let ref_num: string;

	export let _CenPOSEl: HTMLDivElement;
	export let _showInvalidInformationError: Function;
	export let _showSystemError: Function;

	export let cust_id: SiteVerifyRequest_Cust = {
		customercode: '',
		email: ''
	};
</script>

<script lang="ts">
	id += 1;
	if (id > 1) {
		console.error('Only one CenPOS component can be loaded');
	}
	import { onMount, tick } from 'svelte';
	import { CenPOS, CenPOSHandler } from '$lib/client/CenPOS';
	import type { UseCryptoResponse } from '$lib/client/CenPOS';
	import FormGroup from '../sub_components/FormGroup.svelte';
	import { browser } from '$app/environment';
	import type { OnSuccessObj, SiteVerifyRequest_Cust } from '$lib/server/CenPOS';
	import { REST } from '$lib/client/conn';
	import type { CenPOSType } from '$lib/config';
	import { DebugSettings, KTIForm } from '../form';
	import { FormController } from '../actions';
	import Address2, { type IAddress } from './Address2.svelte';
	import { PUBLIC_ENVIRONMENT } from '$env/static/public';
	import * as Sentry from '@sentry/sveltekit';
	import _ from 'lodash';
	export let Config: CenPOSType;
	let paymentSuccess: boolean = false;
	let processingPayment: boolean = false;

	let CenPOSEl: HTMLDivElement;

	// Usage example:
	// createWebpay(document.getElementById("someId"), { url: "https://example.com", params: "someparams" });

	// function createWebpay(el: HTMLElement, options: any) {
	// function onResponse(response: any) {
	// 	responsePayload = response;
	// 	console.log('onResponse', response);
	// }

	//Errors
	function showAmExDiscoverError() {
		KTIForm.error_modal.Show('We accept Visa and Master Card only.  Please try another card.');
	}

	function showInvalidInformationError(errorNum?: string) {
		if (errorNum !== undefined) {
			KTIForm.error_modal.SetRefStr(errorNum);
		}
		KTIForm.error_modal.Show(
			'Payment processing failed. This may be due to incorrect card details (card number, CVV, expiration date). Please check the card and try again, use another card or contact your credit card bank.'
		);
	}
	_showInvalidInformationError = showInvalidInformationError;

	function showSystemError(errorNum?: string) {
		if (errorNum !== undefined) {
			KTIForm.error_modal.Show(
				`There was an error processing your payment (${errorNum}). Please try again later or contact Customer Service at 800-222-0169.`
			);
		} else {
			KTIForm.error_modal.Show(
				`There was an error processing your payment. Please try again later or contact Customer Service at 800-222-0169.`
			);
		}
	}
	_showSystemError = showSystemError;

	function showDeclinedError(errorNum?: string) {
		if (errorNum !== undefined) {
			KTIForm.error_modal.Show(
				`Payment processing failed.  Use another card or contact your credit card bank. (${errorNum})`
			);
		} else {
			KTIForm.error_modal.Show(
				`Payment processing failed.  Use another card or contact your credit card bank.`
			);
		}
	}

	onMount(async () => {
		if (id > 1) {
			return;
		}
		//load porthole
		if (!portholeLoaded && (!portholeLoading || !window.Porthole)) {
			portholeLoading = true; //Prevent multiple loads
			const script = document.createElement('script');
			script.src = 'https://www.cenpos.com/Plugins/porthole.min.js';
			script.async = true;
			document.body.appendChild(script);

			script.onload = () => {
				console.log('Porthole script loaded successfully');
				portholeLoading = false;
				portholeLoaded = true;
			};

			script.onerror = () => {
				console.error('Failed to load the Porthole script');
				portholeLoading = false;
			};
		}

		console.log('Setting CenPOS El', CenPOSEl);
		_CenPOSEl = CenPOSEl;
		//alert(await CenPOS.getSiteVerifyParam());
		//await loadIFrame();
	});

	export function setCustomerID(_customer_id: string, email?: string) {
		if (!_customer_id && !email) {
			//This happens when 'Dealer Not Found' is selected
			if (cust_id.customercode) {
				cust_id.customercode = '';
				//Switch to using email if customer_id is null and email is not null
				if (cust_id.email) {
					CenPOSHandler.loadCenPOSiFrame('', cust_id.email);
				} else {
					//both customer_id and email are empty or null
					//Setting these will hide the CenPOS iframe
					loading = false;
					loaded = false;
				}
			}
			return;
		}
		CenPOSHandler.loadCenPOSiFrame(_customer_id, email);
	}

	export async function process(): Promise<{ success: boolean; response: any }> {
		//For debugging purposes only!
		if (PUBLIC_ENVIRONMENT === 'dev') {
			if (typeof document?.CenPOS_amount !== 'undefined') {
				amount = document.CenPOS_amount;
			} else if (typeof document?.amount !== 'undefined') {
				amount = document?.amount;
			}
		}

		let CenPOSResponse: OnSuccessObj;
		try {
			CenPOSResponse = await CenPOSHandler.submit();
			if (CenPOSResponse.Result != 0) {
				CenPOSHandler.loadCenPOSiFrame();
				return { success: false, response: { msg: 'Unable to process card.' } };
			}
		} catch (e: any) {
			CenPOSHandler.loadCenPOSiFrame();
			if (e.message === 'Timed out waiting for token') {
				showSystemError('A1');
				console.error('CenPOSHandler.submit() timed out waiting for response from CenPOS');
				return { success: false, response: { msg: 'Server error' } };
			}
			return {
				success: false,
				response: { msg: 'Invalid credit card information, please try again.' }
			};
		}

		if (Config.Address2Id) {
			const addressConfig = FormController.get_component(Config.Address2Id);
			if (addressConfig) {
				const addressComp: Address2 = addressConfig.Comp as unknown as Address2;
				const addressVal: IAddress = addressComp.getValue();
				address = {
					address: addressVal.street,
					city: addressVal.city,
					state: addressVal.state,
					zipcode: addressVal.zipcode
				};
			}
		}

		//let customercode = cust_id.customercode;
		if (!cust_id.customercode && !cust_id.email) {
			CenPOSHandler.loadCenPOSiFrame();
			return { success: false, response: { msg: 'No customer code or email provided' } };
		}
		// let siteVerifyData: SiteVerifyRequest = {
		// 	amount,
		// 	invoicenumber: cust_id.customercode || DateTime.now().toFormat('yyMMddHHmmss'),
		// 	customercode,
		// 	email: cust_id.email || undefined,
		// 	type: transactionType,
		// 	tokenid: CenPOSResponse.RecurringSaleTokenId,
		// 	...address
		// };

		// console.log('siteVerifyData', siteVerifyData);
		// let param;
		// try {
		// 	param = await CenPOS.(siteVerifyData);
		// } catch (e) {
		// 	console.log(JSON.stringify(e));
		// 	showSystemError('A2');
		// 	loadCenPOSiFrame();
		// 	return { success: false, response: { msg: 'Server error' } };
		// }
		let res_data: UseCryptoResponse;
		try {
			res_data = await CenPOS.UseCrypto();
		} catch (e) {
			showSystemError('A3');
			CenPOSHandler.loadCenPOSiFrame();
			return { success: false, response: { msg: 'Server error' } };
		}
		let error = CenPOS.checkForError(res_data);
		console.log('CenPOS.checkForError', error);
		if (error !== null) {
			if (error.type === 'AmEx or Discover') {
				showAmExDiscoverError();
			} else if (error.type === 'Invalid information') {
				showInvalidInformationError(error.abi_code);
			} else if (error.type === 'System Error') {
				showSystemError(error.abi_code);
			} else if (error.type === 'Declined') {
				showDeclinedError(error.abi_code);
			}
			console.log('reloading cenpos iframe');
			CenPOSHandler.loadCenPOSiFrame();
			return { success: false, response: res_data };
		}
		console.log(res_data);
		if (res_data.Result === 0) {
			//Save transaction to database
			// let params = new URLSearchParams();
			// for(let [key, value] of Object.entries(res_data)){
			//     params.append(key, value.toString());
			// }
			let cen_pos_table_data: UseCryptoResponse & { Email?: string } = res_data;
			cen_pos_table_data.CustomerCode = cust_id.customercode;
			cen_pos_table_data.Email = cust_id.email;
			let res = await REST.POST('cenpos_trans', cen_pos_table_data);
			//log to sentry not ok
			if (!res.ok) {
				console.error('Failed to save cenpos transaction to database', res);
				Sentry.captureMessage(
					'Failed to save cenpos transaction to database ' + JSON.stringify(cen_pos_table_data)
				);
			}
			// let res = await fetch(`/api/cenpos_trans`, {
			// 	method: 'POST',
			// 	body: JSON.stringify(res_data)
			// });

			ref_num = res_data.ReferenceNumber;
			CenPOSHandler.loadCenPOSiFrame();
			return { success: true, response: res_data };
		} else {
			CenPOSHandler.loadCenPOSiFrame();
			//was it an issue with zip, address or zipcode
			for (let validationResult of res_data.ParameterValidationResultList) {
				if (validationResult.Result.indexOf('Not Match') > -1) {
					return {
						success: false,
						response: { msg: 'An error has occurred with your payment method. Please try again.' }
					};
				}
			}
			return { success: false, response: { msg: 'Unable to process card.' } };
		}
	}

	export async function validate() {
		if (processingPayment && !paymentSuccess) {
			while (processingPayment) {
				await new Promise((r) => setTimeout(r, 100));
			}
			return paymentSuccess;
		}
		processingPayment = true;
		paymentSuccess = false;
		let result = await process();
		console.log('payment success?', result.success, result);
		if (!result.success) {
			KTIForm.loading_backdrop.hide();
			processingPayment = false;
			return paymentSuccess; //result.success == false
		}
		paymentSuccess = true;		
		processingPayment = false;
		return paymentSuccess; //result.success == true
	}

	export function appendData(formData: FormData) {
		formData.set('cenpos_ref_num', ref_num);
		if (!CenPOSHandler.lastSuccessObj) {
			console.log('appendData(): No response payload');
			return;
		}
		for (let [key, value] of Object.entries(CenPOSHandler.lastSuccessObj)) {
			if (['ProtectedCardNumber'].includes(key)) formData.set(key, value.toString());
		}
	}

	export function focus() {
		CenPOSEl.focus();
	}

	// async function validate() {
	// 	if (!paymentSuccess) {
	// 		//Check to make sure we have the required information

	// 		CenPOS.sendPaymentDetails();
	// 	}
	// 	// Wait until
	// }
	let loading = false;
	let loaded = false;
	function _onLoaded() {
		loading = false;
		loaded = true;
	}
	function _onLoading() {
		loading = true;
		loaded = false;
	}
	onLoaded = _onLoaded;
	onLoading = _onLoading;
</script>

<FormGroup {Config}>
	{#if DebugSettings.show_debug}
		<!-- Amount: <input bind:value={amount} type="text" id="CenPOS_amount" /> -->
	{/if}
	<div class="col" style="border-width: 2px;">
		<div style="margin-top:35px"><strong>We accept Visa and Mastercard only.</strong></div>
		<div style:display={loading === true && loaded === false ? 'block' : 'none'}>
			<div
				class="d-flex justify-content-center flex-column align-items-center"
				style="padding: 10px; width: 280px; height: 300px; margin: auto;"
			>
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
				<br />
				<div style="text-align: center; width: 100%; margin-top: 10px;">
					Loading payment processor
				</div>
			</div>
		</div>

		<div
			id="CenPOS"
			bind:this={CenPOSEl}
			style:display={loading === false && loaded === true ? 'block' : 'none'}
		/>
		<div class="d-flex">
			<div><strong>Credit card will be charged at time of submission.</strong></div>
			<div><img src="/visa_mastercard.png" style="margin-left:20px" alt="" /></div>
		</div>
	</div>
</FormGroup>

<!-- <button
	on:click|preventDefault={CenPOS.init(
		transactionType,
		CenPOSEl,
		current_dealer_id,
		onPaymentSuccess,
		OnCancel,
		onOpen,
		onError
	)}>Load</button
>
<button on:click|preventDefault={sendPaymentDetails}>Process</button> -->

<style>
</style>
